import { useQuery } from '@apollo/client/react';
import { kebabCase } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';
import { useEffect, useState } from 'react';

import { useGetActiveScreenBreakpoint } from '@pflegia/ui/hooks/use-get-active-screen-breakpoint';
import Button from '@pflegia/ui/shared/button';

import { DEMAND_GAP_CITY_CLUSTER } from '@shared/constants/homepage-clusters';
import { GET_JOB_COUNT_BY_CITIES } from '@shared/graphql/operations/job-count-by-cities';

import Headline from '../headline/Headline';
import DemandGapCard from './DemandGapCard';

const IMAGE_BASE_PATH = '/assets/homepage-city-cluster/';
// Number of fallback_N.jpg images available; wrap the index so any city count has a valid fallback.
const FALLBACK_IMAGE_COUNT = 6;
// Cities shown on mobile before tapping "show more".
const MOBILE_PREVIEW_COUNT = 4;

type DemandGapClusterProps = {
  titleKey?: string;
  subTitleKey?: string;
};

const DemandGapCluster: React.FC<DemandGapClusterProps> = ({
  titleKey = 'landingPage:demandGapCluster.title',
  subTitleKey = 'landingPage:demandGapCluster.subtitle',
}: DemandGapClusterProps) => {
  const { t } = useTranslation('landingPage', { keyPrefix: 'demandGapCluster' });
  const { isMobile } = useGetActiveScreenBreakpoint();
  const [isExpanded, setIsExpanded] = useState(false);
  // Start with the full list so SSR and first client render match; trim on mobile after mount.
  const [citiesToDisplay, setCitiesToDisplay] = useState(DEMAND_GAP_CITY_CLUSTER);

  useEffect(() => {
    setCitiesToDisplay(isMobile && !isExpanded ? DEMAND_GAP_CITY_CLUSTER.slice(0, MOBILE_PREVIEW_COUNT) : DEMAND_GAP_CITY_CLUSTER);
  }, [isMobile, isExpanded]);

  const { data } = useQuery(GET_JOB_COUNT_BY_CITIES, {
    fetchPolicy: 'cache-first',
    variables: { cities: DEMAND_GAP_CITY_CLUSTER },
  });

  // Backend returns lowercased city names; look them up by lowercased city too.
  const jobCountByCity = data?.cities.reduce<Record<string, number>>((acc, { city, jobCount }) => {
    acc[city] = jobCount;
    return acc;
  }, {});

  return (
    <div className='mx-auto flex flex-col items-center'>
      <Headline i18nKey={titleKey} subTitle={subTitleKey} subTitleClassName='w-full text-base/6 md:w-1/2' showArrow arrowPosition='left' />
      <div className='grid w-full max-w-6xl grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
        {citiesToDisplay.map((city, index) => (
          <DemandGapCard
            key={city}
            city={city}
            imageSrc={`${IMAGE_BASE_PATH}${kebabCase(city)}.jpg`}
            fallbackImageSrc={`${IMAGE_BASE_PATH}fallback_${index % FALLBACK_IMAGE_COUNT}.jpg`}
            jobCount={jobCountByCity?.[city.toLowerCase()] ?? 0}
          />
        ))}
      </div>
      <Button
        label={isExpanded ? t('showLessButton') : t('showMoreButton')}
        colorScheme='secondary'
        className='mt-5 md:hidden'
        onClick={() => setIsExpanded(!isExpanded)}
      />
    </div>
  );
};

export default DemandGapCluster;
